import type { Filters } from '.';

const TYPE_SEP = '$$$';
type TypeIndicator = 'date';

function isPlainObject(value: any): value is Record<string, any> {
  return typeof value === 'object' && value !== null && !Array.isArray(value) && !(value instanceof Date);
}

function serializeDate(date: Date): number {
  return Math.floor(date.getTime() / 1000);
}

function deserializeDate(timestamp: number): Date {
  return new Date(timestamp * 1000);
}

// Serialize a single field k: v -> possibly rename to k$$$date and transform value
export function serializeField(key: string, value: any): [string, any] {
  // Primitive date
  if (value instanceof Date) {
    return [`${key}${TYPE_SEP}date`, serializeDate(value)];
  }

  // Arrays: map elements; if any element is/contains a Date without its own key,
  // mark parent key as $$$date
  if (Array.isArray(value)) {
    let hasDate = false;
    const arr = value.map((item) => {
      if (item instanceof Date) {
        hasDate = true;
        return serializeDate(item);
      }
      if (Array.isArray(item)) {
        const [_, nested] = serializeField('__array__', item); // dummy key to reuse logic
        if (nested !== item) hasDate = true; // nested arrays contained dates
        return nested;
      }
      if (isPlainObject(item)) {
        const obj: Record<string, any> = {};
        for (const [k, v] of Object.entries(item)) {
          const [nk, nv] = serializeField(k, v);
          obj[nk] = nv;
        }
        return obj;
      }
      return item;
    });
    const outKey = hasDate ? `${key}${TYPE_SEP}date` : key;
    return [outKey, arr];
  }

  // Objects: recurse per-field, do not rename this key; nested fields will be renamed as needed
  if (isPlainObject(value)) {
    const obj: Record<string, any> = {};
    for (const [k, v] of Object.entries(value)) {
      const [nk, nv] = serializeField(k, v);
      obj[nk] = nv;
    }
    return [key, obj];
  }

  // Primitives: unchanged
  return [key, value];
}

// Deserialize a single field possibly named with $$$type
export function deserializeField(key: string, value: any): [string, any] {
  const hasMarker = key.includes(TYPE_SEP);
  let baseKey = key;
  let indicator: TypeIndicator | undefined;
  if (hasMarker) {
    const [k, t] = key.split(TYPE_SEP);
    baseKey = k;
    indicator = t as TypeIndicator;
  }

  if (indicator === 'date') {
    // This field holds date-typed content
    if (typeof value === 'number') {
      return [baseKey, deserializeDate(value)];
    }
    if (Array.isArray(value)) {
      const arr = value.map((item) =>
        typeof item === 'number'
          ? deserializeDate(item)
          : Array.isArray(item)
            ? item.map((x) => (typeof x === 'number' ? deserializeDate(x) : x))
            : isPlainObject(item)
              ? deserializeObject(item)
              : item
      );
      return [baseKey, arr];
    }
    if (isPlainObject(value)) {
      return [baseKey, deserializeObject(value)];
    }
    return [baseKey, value];
  }

  // No marker: recursively handle structure
  if (Array.isArray(value)) {
    return [
      baseKey,
      value.map((item) =>
        isPlainObject(item)
          ? deserializeObject(item)
          : Array.isArray(item)
            ? item.map((x) => (isPlainObject(x) ? deserializeObject(x) : x))
            : item
      ),
    ];
  }
  if (isPlainObject(value)) {
    return [baseKey, deserializeObject(value)];
  }
  return [baseKey, value];
}

function serializeObject(input: Record<string, any>): Record<string, any> {
  const out: Record<string, any> = {};
  for (const [k, v] of Object.entries(input)) {
    const [nk, nv] = serializeField(k, v);
    out[nk] = nv;
  }
  return out;
}

function deserializeObject(input: Record<string, any>): Record<string, any> {
  const out: Record<string, any> = {};
  for (const [k, v] of Object.entries(input)) {
    const [nk, nv] = deserializeField(k, v);
    out[nk] = nv;
  }
  return out;
}

// Public APIs
export function serializeItemData(data: any): Record<string, any> {
  if (typeof data !== 'object' || data === null) return data;
  return serializeObject(data);
}

export function deserializeItemData(data: Record<string, any>): Record<string, any> {
  if (typeof data !== 'object' || data === null) return data;
  return deserializeObject(data);
}

export function serializeFilters(filters: Filters): unknown {
  if (filters == null || typeof filters !== 'object') return filters;
  return serializeObject(filters) as any;
}

export function deserializeFilters(filters: unknown): any {
  if (filters == null || typeof filters !== 'object') return filters;
  return deserializeObject(filters as any) as any;
}
